import styled from 'styled-components';

import colours from 'theme/atoms/colours';
import breakpoints from 'theme/atoms/breakpoints';
import * as Text from 'theme/components/Text.style';
import * as Buttons from 'theme/components/Buttons.style';
import { Page } from 'layout/styles/PageLayout.style';

export const PageWrapper = styled(Page)`
  background-color: ${colours.neutral[100]};
  gap: 0;

  @media (max-width: ${breakpoints.laptop}) {
    padding: 80px 0 0;
  }
`;

export const Container = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: 1080px;
  margin: 0 auto 0 0;
  padding: 15px 15px 120px;
  display: flex;
  flex-direction: column;
  gap: 30px;

  @media (max-width: ${breakpoints.laptop}) {
    max-width: none;
    padding: 5px 15px 120px;
    gap: 20px;
  }
`;

export const MobileDateNav = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0 15px;
  width: 100%;

  @media (min-width: ${breakpoints.laptopMin}) {
    display: none;
  }
`;

export const MobileDateTitleSection = styled.div`
  padding: 10px 0 0;
`;

export const ContentGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;

  @media (min-width: ${breakpoints.laptopMin}) {
    display: grid;
    grid-template-columns: 420px 1fr;
    gap: 60px;
    align-items: start;
  }
`;

export const SplitLayout = styled.div`
  align-items: stretch;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;

  @media (min-width: ${breakpoints.laptopMin}) {
    display: grid;
    grid-template-columns: 380px 1fr;
    gap: 0;
    align-items: stretch;
    height: 100%;
    min-height: 0;
    overflow: hidden;
  }

  @media (min-width: ${breakpoints.laptopL}) {
    grid-template-columns: 500px 1fr;
  }
`;

export const LeftColumn = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 15px;
  min-width: 0;
  width: 100%;

  @media (min-width: ${breakpoints.laptopMin}) {
    border-right: 1px solid ${colours.neutral[200]};
    padding: 0;
    gap: 0;
    overflow: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

export const SidebarHeader = styled.div`
  display: none;

  @media (min-width: ${breakpoints.laptopMin}) {
    align-items: center;
    border-bottom: 1px solid ${colours.neutral[200]};
    box-sizing: border-box;
    display: flex;
    gap: 10px;
    justify-content: space-between;
    height: 88px;
    padding: 24px 20px 24px 5px;
    width: 100%;
  }
`;

export const TopPill = styled.div`
  align-items: center;
  background-color: ${colours.neutral[200]};
  border-radius: 1000px;
  display: inline-flex;
  flex-shrink: 0;
  gap: 5px;
  padding: 5px;
`;

export const SidebarContent = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 0 15px 120px;
  width: 100%;

  @media (min-width: ${breakpoints.laptopMin}) {
    gap: 30px;
    padding: 10px;
  }
`;

export const SummaryPrioritiesGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 100%;
`;

export const SidebarSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

export const RightColumn = styled.div`
  display: none;

  @media (min-width: ${breakpoints.laptopMin}) {
    background-color: ${colours.neutral[100]};
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    min-width: 0;
    width: 100%;
    height: 100%;
    overflow-y: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

export const RightColumnHeader = styled.div`
  align-items: center;
  box-sizing: border-box;
  display: flex;
  justify-content: flex-end;
  min-height: 88px;
  padding: 24px 35px;
  width: 100%;
`;

export const RightColumnBody = styled.div<{ $flush?: boolean }>`
  box-sizing: border-box;
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: ${({ $flush }) => ($flush ? '0' : '0 30px 30px')};
  width: 100%;
`;

export const RightColumnPreferences = styled.div`
  align-items: stretch;
  display: flex;
  flex-direction: column;
  margin: auto;
  max-width: 600px;
  padding: 30px 0;
  width: 100%;
`;

export const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  min-width: 0;
`;

export const DateTitleRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;

  @media (min-width: ${breakpoints.laptopMin}) {
    min-height: 50px;
  }
`;

export const DateTitle = styled(Text.H3)`
  color: ${colours.neutral[900]};
  padding-top: 10px;

  @media (min-width: ${breakpoints.laptopMin}) {
    font-size: 24px;
    line-height: 28px;
    letter-spacing: -0.5px;
    padding-top: 0;
  }
`;

export const ActivityHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;

  @media (min-width: ${breakpoints.laptopMin}) {
    min-height: 50px;
  }
`;

export const ActivityTitle = styled(Text.H5)`
  color: ${colours.neutral[900]};
`;

export const EventList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 100%;
`;

export const EventGroupHeader = styled('button')`
  align-items: center;
  appearance: none;
  background: none;
  border: none;
  color: ${colours.neutral[900]};
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  margin: 0;
  padding: 10px 0;
  width: 100%;

  &:focus-visible {
    outline: 2px solid ${colours.neutral[400]};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

export const EventGroupHeaderChevron = styled('span')<{ $expanded: boolean }>`
  align-items: center;
  display: inline-flex;
  height: 20px;
  justify-content: center;
  transform: rotate(${({ $expanded }) => ($expanded ? '0deg' : '180deg')});
  transition: transform 0.2s ease;
  width: 20px;

  svg {
    height: 20px;
    width: 20px;
  }
`;

export const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px 0;
  width: 100%;
`;

export const EmptyStateResetButton = styled(Buttons.SecondaryLight)`
  background-color: ${colours.shade[0]};

  &:hover,
  &:focus {
    background-color: ${colours.neutral[100]};
  }
`;
